<template>
    <l-card-content>
        <l-headline
            :tabs="tabs"
            :title="$t('surveys.details.titleQuestions')"
            :sub-title="survey.name ? survey.name : $t('surveys.details.labelNewSurvey')"
            :auto-save-text="autoSavingText"
            btn-test-id="surveys-edit-questions"
        >
        </l-headline>

        <hr class="visible-xs" />

        <div class="row">
            <div
                class="col-md-4 text-center clickable"
                tabindex="0"
                data-test-id="survey-edit-toggle-template"
                @click="
                    () => {
                        showTemplates = !showTemplates;
                        showQuestionBank = false;
                    }
                "
                @keyup.enter="
                    () => {
                        showTemplates = !showTemplates;
                        showQuestionBank = false;
                    }
                "
            >
                <img :src="booksIcon" alt="" height="100" />
                <h4 v-show="!showTemplates" class="color-primary">
                    <strong>
                        <span>{{ $t("surveys.details.btnShowTemplates") }}</span>
                    </strong>

                    <i class="fal fa-caret-right" style="margin-left: 9px;"></i>
                </h4>
                <h4 v-show="showTemplates" class="color-primary">
                    <strong>
                        <span>{{ $t("surveys.details.btnHideTemplates") }}</span>
                    </strong>

                    <i class="fal fa-caret-down" style="margin-left: 9px;"></i>
                </h4>
            </div>
            <div
                class="col-md-4 text-center clickable"
                tabindex="0"
                data-test-id="survey-edit-question-library"
                @click="
                    () => {
                        showQuestionBank = !showQuestionBank;
                        showTemplates = false;
                        selectedCategory = 'Engagement';
                    }
                "
                @keyup.enter="
                    () => {
                        showQuestionBank = !showQuestionBank;
                        showTemplates = false;
                        selectedCategory = 'Engagement';
                    }
                "
            >
                <img :src="settingsIcon" alt="" height="100" />
                <h4 v-show="!showQuestionBank" class="color-primary">
                    <strong
                        ><span>{{ $t("surveys.details.btnShowLibrary") }}</span></strong
                    >

                    <i class="fal fa-caret-right" style="margin-left: 9px;"></i>
                </h4>
                <h4 v-show="showQuestionBank" class="color-primary">
                    <strong>
                        <span>{{ $t("surveys.details.btnHideLibrary") }}</span>
                    </strong>

                    <i class="fal fa-caret-down" style="margin-left: 9px;"></i>
                </h4>
            </div>
            <div
                class="col-md-4 text-center clickable"
                tabindex="0"
                data-test-id="survey-edit-question-add-custom-question"
                @click="addQuestionOrSection('survey')"
                @keyup.enter="addQuestionOrSection('survey')"
            >
                <img :src="chatIcon" alt="" height="100" />
                <h4 class="color-primary">
                    <strong>
                        <span>{{ $t("surveys.details.labelAddCustomQuestion") }}</span>
                    </strong>

                    <i class="fal fa-caret-right" style="margin-left: 9px;"></i>
                </h4>
            </div>
        </div>

        <div v-show="showTemplates">
            <hr />

            <div class="row">
                <div
                    v-for="(template, idx) in surveyTemplates"
                    :key="idx"
                    data-test-class="survey-question-templates-item"
                    class="col-md-6"
                >
                    <button
                        v-if="showOverrideWarning != template.title"
                        class="btn btn-primary"
                        tabindex="0"
                        @click="initSurveyTemplate(template)"
                        @keyup.enter="initSurveyTemplate(template)"
                    >
                        <i class="fal fa-list"></i>
                        <span>{{
                            $t(template.title, { questions: template.questions.length })
                        }}</span>
                    </button>
                    <button
                        v-else
                        class="btn btn-danger btn-raised"
                        tabindex="0"
                        @click="initSurveyTemplate(template, true)"
                        @keyup.enter="initSurveyTemplate(template, true)"
                    >
                        <i class="fal fa-exclamation-triangle"></i>
                        <span data-test-id="survey-edit-override">{{
                            $t("surveys.details.btnOverrideTemplate", {
                                count: survey.questions.length,
                            })
                        }}</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- question bank -->

        <div v-show="showQuestionBank">
            <hr />

            <!-- groupBy: 'categoryName' -->
            <div v-for="(questions, category) in questionBankByCategory" :key="category">
                <h4
                    class="clickable"
                    tabindex="0"
                    data-test-class="survey-edit-question-bank"
                    @click="
                        () => {
                            selectedCategory = selectedCategory != category ? category : '';
                        }
                    "
                    @keyup.enter="
                        () => {
                            selectedCategory = selectedCategory != category ? category : '';
                        }
                    "
                >
                    <i v-if="selectedCategory != category" class="fal fa-caret-right"></i>
                    <i v-else class="fal fa-caret-down"></i>

                    <strong style="margin-left: 9px;"
                        >{{ category }} ({{ questions.length }})</strong
                    >
                </h4>
                <div class="hr-small"></div>
                <div
                    v-for="(question, index) in questions"
                    v-show="selectedCategory == category"
                    :key="index"
                    class="row mouseover-parent"
                >
                    <div class="col-md-2">
                        <button
                            class="btn btn-sm btn-primary"
                            :class="{
                                'btn-raised': (survey.questions || [])
                                    .filter(q => q.skill && !q.isRemoved)
                                    .some(q => q.skill._id === question._id),
                            }"
                            tabindex="0"
                            data-test-class="survey-edit-question-bank-q"
                            @click="() => addQuestionFromLibrary(question)"
                            @keyup.enter="addQuestionFromLibrary(question)"
                        >
                            <span
                                v-if="
                                    !(survey.questions || [])
                                        .filter(q => q.skill && !q.isRemoved)
                                        .some(q => q.skill._id === question._id)
                                "
                            >
                                <span>{{ $t("surveys.details.btnAddQuestion") }}</span>
                            </span>
                            <span v-else>
                                <i class="fal fa-check"></i>
                                <span style="margin-left: 9px;">{{
                                    $t("surveys.details.btnAdded")
                                }}</span>
                            </span>
                        </button>
                    </div>
                    <div class="col-md-10">
                        <div style="margin-top: 10px">
                            {{
                                question.name
                                    ? question.name.replace("%ACCOUNTNAME%", accountName)
                                    : ""
                            }}
                            <span
                                class="pull-right color-primary mouseover clickable"
                                tabindex="0"
                                @click="scrollToBottom()"
                                @keyup.enter="scrollToBottom()"
                            >
                                <i class="fal fa-caret-down"></i>
                                <span style="margin-left: 9px;">{{
                                    $t("surveys.details.btnPreviewQuestionnaire")
                                }}</span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Question list -->
        <div v-show="survey.questions && survey.questions.length">
            <hr />
            <h4 class="color-dark">
                <strong
                    ><span> {{ $t("surveys.details.labelQuestionnairePreview") }}</span>
                </strong>
                <span class="color-grey">
                    <i class="fal fa-hand-pointer r-default-margin-s l-default-margin-s"></i>
                    {{ $t("surveys.details.labelDragNDrop") }}
                </span>
            </h4>
            <hr />
        </div>

        <!--selected question of this survey-->

        <draggable
            v-model="survey.questions"
            tag="ul"
            handle=".handle"
            class="dropzone"
            style="padding: 0;"
            @change="changeOrder"
        >
            <template v-for="(question, index) in survey.questions">
                <li
                    v-show="question.skill && !question.isRemoved"
                    :key="index"
                    data-test-class="survey-questions-item"
                >
                    <!-- adding a new section -->
                    <div v-show="question.skill.type == 'surveySection'">
                        <div class="form-group" style="margin-top: 5px; margin-bottom: -10px;">
                            <div class="input-group">
                                <i
                                    class="fal fa-arrows-v handle"
                                    style="
                                                position: absolute;
                                                left: 0px;
                                                top: 12px;
                                                right: auto;
                                                z-index: 11;
                                                display: inline-block;
                                                padding: 0 10px;
                                                cursor: move;
                                            "
                                ></i>
                                <l-input-text
                                    v-model="question.skill.name"
                                    input-class="section"
                                    style="margin-left: 25px;"
                                    :label="``"
                                    :placeholder="$t(`surveys.details.labelSection`)"
                                    @input="
                                        () => {
                                            updateSkillTranslation(question.skill, 'name');
                                            debouncedUpdateSurvey();
                                        }
                                    "
                                ></l-input-text>

                                <span
                                    v-b-popover.hover.top="$t(`skills.translate.iconPopover`)"
                                    class="tw-absolute tw-cursor-pointer tw-text-lg tw-text-gray-400 hover:tw-text-gray-900"
                                    style="top: 7px; right: 48px; z-index: 3;"
                                    tabindex="0"
                                    @click="openTranslationModal(question.skill, 'name')"
                                    @keyup.enter="openTranslationModal(question.skill, 'name')"
                                >
                                    <i class="fal fa-language"></i>
                                </span>

                                <span
                                    class="input-group-addon clickable"
                                    tabindex="0"
                                    @click="removeQuestion(question)"
                                    @keyup.enter="removeQuestion(question)"
                                >
                                    <i class="fal fa-trash color-primary"></i>
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- question -->
                    <div
                        v-show="
                            question.skill.type == 'survey' ||
                                question.skill.type == 'surveyTemplate'
                        "
                        class="panel"
                        :class="[isEditMode(question) ? 'panel-primary' : 'panel-default']"
                    >
                        <div dnd-handle class="panel-heading">
                            <div class="row mouseover-parent">
                                <div
                                    class="col-md-10 clickable handle"
                                    tabindex="0"
                                    @click="
                                        () => {
                                            $set(question, 'isEditing', true);
                                        }
                                    "
                                    @keyup.enter="
                                        () => {
                                            $set(question, 'isEditing', true);
                                        }
                                    "
                                >
                                    <span>
                                        <i
                                            v-show="question.skill.answerType == 'structured'"
                                            class="fal fa-comment-check"
                                        >
                                        </i>
                                        <i
                                            v-show="question.skill.answerType == 'structuredOnly'"
                                            class="fal fa-chart-bar"
                                        >
                                        </i>
                                        <i
                                            v-show="question.skill.answerType == 'unstructured'"
                                            class="fal fa-comment-lines"
                                        ></i>
                                        <i
                                            v-show="question.skill.answerType == 'multipleChoice'"
                                            class="fal fa-ballot"
                                        >
                                        </i>
                                    </span>
                                    <span> <i></i></span>

                                    <span
                                        v-b-popover.hover.right="{
                                            content:
                                                question.categoryName &&
                                                question.categoryName.toUpperCase(),
                                        }"
                                        style="padding-left: 8px;"
                                    >
                                        {{
                                            question.skill && question.skill.name
                                                ? question.skill.name.replace(
                                                      "%ACCOUNTNAME%",
                                                      accountName,
                                                  )
                                                : ""
                                        }}
                                    </span>

                                    <span
                                        v-show="
                                            question.skill.type != 'surveyTemplate' &&
                                                (survey.status == 'draft' || question.unsaved)
                                        "
                                        class="mouseover"
                                    >
                                        <i
                                            class="fal fa-pencil color-primary l-default-margin-s"
                                        ></i>
                                    </span>
                                </div>
                                <div class="col-md-2 next-round">
                                    <span
                                        v-b-popover.hover.left="{
                                            content: $t('surveys.details.labelAskedNextRound'),
                                        }"
                                        :class="{
                                            'visibility-hidden': !question.askInNextRound,
                                        }"
                                        :style="{ border: 0 }"
                                    >
                                        <i class="fal fa-calendar-check color-primary"></i>
                                    </span>
                                    <button
                                        v-if="!isEditMode(question)"
                                        v-b-popover.hover.top="
                                            isQuestionRequired(question)
                                                ? $t('surveys.details.labelAnswerRequired')
                                                : $t('surveys.details.labelAnswerOptional')
                                        "
                                        class="tw-border-0 tw-bg-transparent clickable tw-p-0"
                                        data-test-class="survey-edit-toggle-required-question"
                                        @click="onRequiresAnswerToggle(question)"
                                    >
                                        <i
                                            class="fal color-primary"
                                            :class="{
                                                'fa-question-square': !isQuestionRequired(question),
                                                'fa-exclamation-square': isQuestionRequired(
                                                    question,
                                                ),
                                            }"
                                        />
                                    </button>
                                    <b-dropdown
                                        v-b-popover.hover.top="{
                                            content: question.isPaused
                                                ? $t('surveys.details.labelStatusPaused')
                                                : question.isPinned
                                                ? $t('surveys.details.labelStatusPinned')
                                                : $t('surveys.details.labelStatusNormal'),
                                        }"
                                        style="margin: 0"
                                        class="dropdown"
                                    >
                                        <template #button-content>
                                            <span class="color-primary" style="font-size: 18px;">
                                                <i
                                                    v-if="
                                                        !isEditMode(question) &&
                                                            !question.isPaused &&
                                                            !question.isPinned
                                                    "
                                                    class="fal fa-cog right only"
                                                ></i>
                                                <i
                                                    v-if="
                                                        !isEditMode(question) &&
                                                            question.isPaused &&
                                                            !question.isPinned
                                                    "
                                                    class="fal fa-pause right only"
                                                ></i>
                                                <i
                                                    v-if="
                                                        !isEditMode(question) &&
                                                            !question.isPaused &&
                                                            question.isPinned
                                                    "
                                                    class="fal fa-thumbtack right only"
                                                ></i>
                                            </span>
                                        </template>

                                        <b-dropdown-item
                                            v-if="!isEditMode(question) && !question.isPaused"
                                            v-b-popover.hover.top="{
                                                content: $t('surveys.details.labelPause'),
                                            }"
                                            class="clickable"
                                            @click="pauseQuestion(question)"
                                        >
                                            <i class="fal fa-pause color-primary"></i>
                                            <span>{{ $t("surveys.details.optionPause") }}</span>
                                        </b-dropdown-item>
                                        <b-dropdown-item
                                            v-if="!isEditMode(question) && question.isPaused"
                                            v-b-popover.hover.top="{
                                                content: $t('surveys.details.labelUnpause'),
                                            }"
                                            class="clickable"
                                            @click="unPauseQuestion(question)"
                                        >
                                            <i class="fal fa-play color-primary"></i>
                                            <span>{{ $t("surveys.details.optionUnPause") }}</span>
                                        </b-dropdown-item>
                                        <b-dropdown-item
                                            v-if="!isEditMode(question) && !question.isPinned"
                                            v-b-popover.hover.top="{
                                                content: $t('surveys.details.labelPin'),
                                            }"
                                            class="clickable"
                                            @click="() => pinQuestion(question)"
                                        >
                                            <i class="fal fa-thumbtack color-primary"></i>
                                            <span>{{ $t("surveys.details.optionPin") }}</span>
                                        </b-dropdown-item>
                                        <b-dropdown-item
                                            v-if="!isEditMode(question) && question.isPinned"
                                            v-b-popover.hover.top="{
                                                content: $t('surveys.details.labelUnPin'),
                                            }"
                                            class="clickable"
                                            @click="() => unpinQuestion(question)"
                                        >
                                            <i class="fal fa-thumbtack color-primary"></i>
                                            <span>{{ $t("surveys.details.optionUnPin") }}</span>
                                        </b-dropdown-item>
                                    </b-dropdown>

                                    <span
                                        v-show="
                                            !isEditMode(question) &&
                                                question.skill.answerType === 'structured'
                                        "
                                        v-b-popover.hover.top="{
                                            content: $t(
                                                'surveys.details.labelImpactDriverQuestion',
                                            ),
                                        }"
                                        data-test-class="survey-edit-question-impact-driver-toggle"
                                        class="clickable"
                                        tabindex="0"
                                        @click="() => toggleImpactDriver(question)"
                                        @keyup.enter="toggleImpactDriver(question)"
                                    >
                                        <i
                                            v-if="question.isImpactDriver"
                                            class="fal fa-check-square color-primary"
                                        ></i>
                                        <i v-else class="fal fa-square color-primary"></i>
                                    </span>
                                    <span
                                        v-show="
                                            !isEditMode(question) &&
                                                question.skill.answerType !== 'structured'
                                        "
                                        class="clickable"
                                    >
                                        <i
                                            v-show="question.skill.answerType !== 'structured'"
                                            class="fal fa-square color-grey"
                                            style="cursor:not-allowed"
                                        ></i>
                                    </span>

                                    <span
                                        v-show="!isEditMode(question)"
                                        v-b-popover.hover.top="{
                                            content: $t('surveys.details.labelRemove'),
                                        }"
                                        class="clickable"
                                        tabindex="0"
                                        data-test-class="survey-edit-question-remove"
                                        @click="removeQuestion(question)"
                                        @keyup.enter="removeQuestion(question)"
                                    >
                                        <i class="fal fa-trash color-primary"></i>
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div v-show="isEditMode(question)" ref="custom" class="panel-body">
                            <div
                                v-show="question.skill.type !== 'surveyTemplate'"
                                class="tw-relative"
                            >
                                <l-input-text
                                    v-model="question.skill.name"
                                    data-test-id="survey-edit-question-name"
                                    style="margin-top: 24px;"
                                    :label="
                                        !question._id
                                            ? $t('surveys.details.labelQuestionName')
                                            : $t('surveys.details.labelQuestionName') +
                                              $t('surveys.details.labelQuestionExists')
                                    "
                                    :placeholder="$t(`surveys.details.placeholderType`)"
                                    @input="
                                        () => {
                                            updateSkillTranslation(question.skill, 'name');
                                            debouncedUpdateSurvey();
                                        }
                                    "
                                ></l-input-text>

                                <span
                                    v-b-popover.hover.top="$t(`skills.translate.iconPopover`)"
                                    class="tw-absolute tw-cursor-pointer tw-text-lg tw-text-gray-400 hover:tw-text-gray-900"
                                    style="top: 7px; right: 14px;"
                                    tabindex="0"
                                    @click="openTranslationModal(question.skill, 'name')"
                                    @keyup.enter="openTranslationModal(question.skill, 'name')"
                                >
                                    <i class="fal fa-language"></i>
                                </span>
                            </div>

                            <div
                                v-show="question.skill.type !== 'surveyTemplate'"
                                class="tw-grid tw-gap-8 tw-mt-4"
                                :class="{
                                    'tw-grid-cols-2': question.skill.category == 'new',
                                }"
                            >
                                <div>
                                    <l-select
                                        v-model="question.skill.category"
                                        :label="$t('surveys.details.labelQuestionCategory')"
                                        data-test-class="survey-edit-question-survey-category"
                                        :options="[
                                            {
                                                value: 'null',
                                                text: $t('surveys.details.optionNoCategory'),
                                            },
                                            {
                                                value: 'new',
                                                text: $t('surveys.details.optionNewCategory'),
                                            },
                                            ...category.map(c => ({
                                                value: c._id,
                                                text: c.name,
                                            })),
                                        ]"
                                        @change="
                                            e => {
                                                if (e.target.value === 'new') {
                                                    // user wants to add a new cateogry, we shouldn't auto save
                                                    $forceUpdate();
                                                } else {
                                                    updateSurvey();
                                                }
                                            }
                                        "
                                    />
                                </div>

                                <div
                                    v-if="question.skill.category == 'new'"
                                    class="tw-flex tw-space-x-4 tw-justify-center"
                                    :class="{
                                        'tw-items-start': canAddQuestionCategory,
                                        'tw-items-center': !canAddQuestionCategory,
                                    }"
                                >
                                    <template v-if="canAddQuestionCategory">
                                        <l-input-text
                                            v-model="newCategoryName"
                                            :label="$t('surveys.details.labelNewCategory')"
                                            :placeholder="$t(`surveys.details.placeholderType`)"
                                            class="tw-flex-1"
                                        ></l-input-text>

                                        <span
                                            class="color-primary tw-cursor-pointer tw-flex-shrink-0 tw-mt-2"
                                            tabindex="0"
                                            @click="createCategory(newCategoryName, question)"
                                            @keyup.enter="createCategory(newCategoryName, question)"
                                        >
                                            <i class="fal fa-save tw-mr-2"></i>
                                            {{ $t("surveys.details.btnSaveCategory") }}
                                        </span>
                                    </template>
                                    <template v-else>
                                        <i class="fal fa-info-circle" />
                                        <span class="tw-bg-gray-500 tw-text-sm">{{
                                            $t("surveys.details.btnSaveCategoryDisabledHint")
                                        }}</span>
                                    </template>
                                </div>
                            </div>

                            <div class="row tw-mt-8 tw-mb-4">
                                <div class="col-md-3">
                                    <l-select
                                        id="answer-type"
                                        v-model="question.skill.answerType"
                                        :label="$t('surveys.details.labelAnswerType')"
                                        data-test-class="survey-edit-question-survey-category"
                                        :options="optionsAnswerType"
                                        :disabled="
                                            question.skill.type === 'surveyTemplate' ||
                                                (survey.status !== 'draft' &&
                                                    question.skill._id !== '')
                                        "
                                        @change="e => onAnswerTypeChange(question, e)"
                                    />
                                </div>
                                <div
                                    v-show="
                                        ['structured', 'structuredOnly'].includes(
                                            question.skill.answerType,
                                        )
                                    "
                                    class="col-md-9 row"
                                >
                                    <div class="col-md-3">
                                        <l-select-grouped
                                            id="scaleType"
                                            :value.sync="question.scaleType"
                                            :disabled="
                                                survey.status !== 'draft' &&
                                                    question.skill._id !== ''
                                            "
                                            :data="
                                                question.skill.type === 'surveyTemplate'
                                                    ? templateQuestionsScaleType
                                                    : customQuestionsScaleType
                                            "
                                            @change="updateSurvey"
                                        />
                                    </div>
                                    <div
                                        v-if="question.scaleType && question.scaleType !== 'nps'"
                                        class="col-md-9 tw-mt-2 tw-text-sm tw-text-gray-400"
                                    >
                                        {{
                                            $t(
                                                `surveys.participate.scaleLabels.${question.scaleType}.1`,
                                            )
                                        }}
                                        <i class="fal fa-arrow-right tw-mr-2 tw-ml-2"></i>
                                        {{
                                            $t(
                                                `surveys.participate.scaleLabels.${question.scaleType}.5`,
                                            )
                                        }}
                                    </div>
                                    <div
                                        v-else
                                        class="col-md-9 tw-mt-2 tw-text-sm tw-text-gray-400"
                                    >
                                        {{ $t(`surveys.participate.zeroScoreMeaning`) }}
                                        <i class="fal fa-arrow-right tw-mr-2 tw-ml-2"></i>
                                        {{ $t(`surveys.participate.tenScoreMeaning`) }}
                                    </div>
                                </div>
                            </div>

                            <div
                                v-show="question.skill.answerType === 'multipleChoice'"
                                class="tw-mt-8"
                            >
                                <div>
                                    <div class="form-group label-floating">
                                        <div class="input-group">
                                            <label class="control-label">
                                                <span>{{
                                                    $t("surveys.details.labelMultipleChoiceOptions")
                                                }}</span>
                                            </label>
                                        </div>
                                    </div>
                                    <div
                                        v-for="(option, index) in question.skill
                                            .multipleChoiceOptions"
                                        :key="index"
                                    >
                                        <div class="row">
                                            <div class="col-md-11 tw-relative">
                                                <l-input-text
                                                    v-model="option.title"
                                                    :label="``"
                                                    :placeholder="``"
                                                    @input="
                                                        () => {
                                                            $forceUpdate();
                                                            updateSkillTranslation(
                                                                question.skill,
                                                                `multipleChoiceOptions.${index}.title`,
                                                            );
                                                            debouncedUpdateSurvey();
                                                        }
                                                    "
                                                ></l-input-text>
                                                <span
                                                    v-b-popover.hover.top="
                                                        $t(`skills.translate.iconPopover`)
                                                    "
                                                    class="tw-absolute tw-cursor-pointer tw-text-lg tw-text-gray-400 hover:tw-text-gray-900"
                                                    style="top: 7px; right: 35px; z-index: 3;"
                                                    tabindex="0"
                                                    @click="
                                                        openTranslationModal(
                                                            question.skill,
                                                            `multipleChoiceOptions.${index}.title`,
                                                        )
                                                    "
                                                    @keyup.enter="
                                                        openTranslationModal(
                                                            question.skill,
                                                            `multipleChoiceOptions.${index}.title`,
                                                        )
                                                    "
                                                >
                                                    <i class="fal fa-language"></i>
                                                </span>
                                            </div>
                                            <div class="col-md-1">
                                                <i
                                                    v-show="
                                                        question.skill.multipleChoiceOptions
                                                            .length > 1
                                                    "
                                                    class="fal fa-trash fake-link clickable"
                                                    tabindex="0"
                                                    @click="
                                                        removeMultipleChoiceOption(question, index)
                                                    "
                                                    @keyup.enter="
                                                        removeMultipleChoiceOption(question, index)
                                                    "
                                                ></i>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="row">
                                        <div class="col-md-1 col-md-offset-11">
                                            <i
                                                class="fal fa-plus fake-link clickable"
                                                tabindex="0"
                                                @click="
                                                    () => {
                                                        if (
                                                            !Array.isArray(
                                                                question.skill
                                                                    .multipleChoiceOptions,
                                                            )
                                                        ) {
                                                            question.skill.multipleChoiceOptions = [];
                                                        }

                                                        question.skill.multipleChoiceOptions.push({
                                                            title: '',
                                                        });
                                                        $forceUpdate(); // we don't need to autoSave this as that should happen when question.title changes.
                                                    }
                                                "
                                                @keyup.enter="
                                                    () => {
                                                        if (
                                                            !Array.isArray(
                                                                question.skill
                                                                    .multipleChoiceOptions,
                                                            )
                                                        ) {
                                                            question.skill.multipleChoiceOptions = [];
                                                        }

                                                        question.skill.multipleChoiceOptions.push({
                                                            title: '',
                                                        });
                                                        $forceUpdate();
                                                    }
                                                "
                                            ></i>
                                        </div>
                                    </div>
                                </div>

                                <br />

                                <div class="row">
                                    <div class="col-md-10">
                                        <div class="togglebutton">
                                            <label>
                                                <input
                                                    :checked="question.skill.allowMultipleAnswers"
                                                    type="checkbox"
                                                    @click="
                                                        () => {
                                                            question.skill.allowMultipleAnswers = !question
                                                                .skill.allowMultipleAnswers;
                                                            updateSurvey();
                                                        }
                                                    "
                                                />
                                                <span class="toggle"></span>
                                                <span class="color-dark">
                                                    <span>{{
                                                        $t(
                                                            "surveys.details.toggleQuestionAllowMultipleAnswers",
                                                        )
                                                    }}</span>
                                                </span>
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <hr />
                            </div>

                            <div class="row">
                                <div class="col-md-10">
                                    <div class="togglebutton">
                                        <label>
                                            <input
                                                :checked="question.isRequired"
                                                type="checkbox"
                                                @click="onRequiresAnswerToggle(question)"
                                            />
                                            <span class="toggle"></span>
                                            <span class="color-dark">
                                                <span>{{
                                                    $t("surveys.details.toggleRequiredAnswer")
                                                }}</span>
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <hr />

                            <button
                                data-test-class="survey-edit-question-close"
                                :disabled="question.skill.category == 'new'"
                                class="btn btn-sm btn-primary btn-raised pull-right"
                                @click="
                                    () => {
                                        $set(question, 'isEditing', false);
                                    }
                                "
                            >
                                <span>{{ $t("surveys.details.btnSaveQuestion") }}</span>
                            </button>

                            <button
                                class="btn btn-sm btn-primary pull-right"
                                @click="removeQuestion(question)"
                            >
                                <span>{{ $t("surveys.details.btnRemoveQuestion") }}</span>
                            </button>
                        </div>
                    </div>
                </li>
            </template>
        </draggable>

        <div v-show="survey.status != 'closed'">
            <hr />
            <!-- TODO needs to habe a better way to handle this  -->
            <div v-show="error && error.message" id="error" class="alert alert-info">
                <span>{{ error.message }}</span>
            </div>

            <div class="row">
                <div class="col-md-3">
                    <button
                        class="btn btn-framed btn-block"
                        @click="addQuestionOrSection('survey')"
                    >
                        <span>{{ $t("surveys.details.labelAddCustomQuestion") }}</span>
                    </button>
                </div>
                <div class="col-md-3">
                    <button
                        class="btn btn-framed btn-block"
                        @click="addQuestionOrSection('surveySection')"
                    >
                        <span> {{ $t("surveys.details.labelAddSection") }}</span>
                    </button>
                </div>
                <div class="col-md-3">
                    <button class="btn btn-framed" style="float: right" @click="openPreview">
                        <span> {{ $t("surveys.details.labelOpenPreview") }}</span>
                    </button>
                </div>
                <div class="col-md-3">
                    <button
                        class="btn btn-primary btn-raised btn-block"
                        type="button"
                        @click="$router.push(`/surveys/edit/${$route.params.id}/timing`)"
                    >
                        <span>{{ $t("surveys.details.btnSaveChanges") }}</span>
                    </button>
                </div>
            </div>

            <l-translation-modal
                v-show="showTranslationModal"
                :existing-translations="translations"
                @cancel="showTranslationModal = false"
                @confirm="updateTranslations"
            />
        </div>
    </l-card-content>
</template>

<script>
import { mapGetters } from "vuex";

import getVal from "@/utils/get-nested-object-lookup";

import LCardContent from "@/components/LCardContent";
import LHeadline from "@/components/LHeadline";
import LInputText from "@/components/LInputText";
import LSelect from "@/components/LSelect";
import LSelectGrouped from "@/components/LSelectGrouped";
import LTranslationModal from "@/components/LTranslationModal";

import icon from "@/img/icons/surveys.png";
import booksIcon from "@/img/icons/badge_book.png";
import settingsIcon from "@/img/icons/badge_settings.png";
import chatIcon from "@/img/icons/badge_chat.png";

import Draggable from "vuedraggable";
import debounce from "lodash.debounce";
import surveyTemplates from "@/config/surveyTemplates";

export default {
    components: {
        LCardContent,
        LHeadline,
        LInputText,
        Draggable,
        LSelect,
        LSelectGrouped,
        LTranslationModal,
    },
    props: {
        autoSavingText: {
            type: String,
            default: "",
        },
        openHelpHC: {
            type: Function,
            default: () => {},
        },
    },
    data() {
        return {
            showTemplates: false,
            showQuestionBank: false,
            selectedCategory: "",
            newCategoryName: "",
            showOverrideWarning: false,

            survey: {},

            showTranslationModal: false,
            translations: {},
        };
    },
    computed: {
        ...mapGetters({
            rights: "users/current/rights",
            user: "users/current/user",
            locale: "users/current/locale",
            teamSettings: "users/current/teamSettings",

            usersSearchList: "users/search/list",
            teamsSearchList: "teams/search/list",
            getUserList: "users/list/list",

            questionBank: "surveys/surveys/questionBank",
            category: "surveys/surveys/category", // survey category list.
            getDetail: "surveys/surveys/detail",

            error: "surveys/surveys/error",
        }),
        tabs() {
            return [
                {
                    tabClass: "space",
                    show: true,
                },
                {
                    show: true,
                    label: this.$t("surveys.details.showHC"),
                    handler: this.openHelpHC,
                    isButton: true,
                },
            ];
        },
        optionsAnswerType() {
            return [
                {
                    value: "unstructured",
                    text: this.$t("surveys.details.optionUnstructured"),
                },
                {
                    value: "structuredOnly",
                    text: this.$t("surveys.details.optionStructuredOnly"),
                },
                {
                    value: "structured",
                    text: this.$t("surveys.details.optionStructured"),
                },
                {
                    value: "multipleChoice",
                    text: this.$t("surveys.details.optionMultipleChoice"),
                },
            ];
        },

        templateQuestionsScaleType() {
            return {
                "surveys.details.label010Scale": {
                    nps: this.$t("surveys.details.optionNps"),
                },
                "surveys.details.label5pointScale": {
                    likert: this.$t("surveys.details.optionLikert"),
                },
            };
        },
        customQuestionsScaleType() {
            return {
                "surveys.details.label010Scale": {
                    nps: this.$t("surveys.details.optionNps"),
                },
                "surveys.details.label5pointScale": {
                    likert: this.$t("surveys.details.optionLikert"),
                    satisfaction: this.$t("surveys.details.optionSatisfaction"),
                    quality: this.$t("surveys.details.optionQuality"),
                    frequency: this.$t("surveys.details.optionFrequency"),
                    performance: this.$t("surveys.details.optionPerformance"),
                    importance: this.$t("surveys.details.optionImportance"),
                    focus: this.$t("surveys.details.optionFocus"),
                },
            };
        },

        questionBankByCategory() {
            return this.questionBank.reduce((rv, x) => {
                (rv[x["categoryName"]] = rv[x["categoryName"]] || []).push(x);
                return rv;
            }, {});
        },

        particpantsPool() {
            const exists = this.survey.participants;
            return [
                ...this.usersSearchList
                    .filter(
                        user =>
                            user._id !== this.user._id &&
                            !exists.map(p => p._id).includes(user._id),
                    )
                    .map(user => ({
                        _id: user._id,
                        type: "user",
                        firstname: user.firstname,
                        lastname: user.lastname,
                        name: user.firstname + " " + user.lastname,
                        desc: user.teamRole.title,
                        picture: user.picture,
                    })),
                ...this.teamsSearchList.map(team => ({
                    _id: team._id,
                    type: "team",
                    name: team.name,
                })),
            ];
        },

        accountName() {
            // company name
            return this.teamSettings.name;
        },

        canAddQuestionCategory() {
            return this.rights?.A_SKILL;
        },
    },
    watch: {
        "$route.params.id": {
            deep: true,
            immediate: true,
            handler(id) {
                this.survey = this.getDetail(id);
            },
        },
    },

    created() {
        this.icon = icon;
        this.booksIcon = booksIcon;
        this.settingsIcon = settingsIcon;
        this.chatIcon = chatIcon;

        this.surveyTemplates = surveyTemplates;
        this.$parent.$on("updated", () => {
            this.survey = this.getDetail(this.survey._id);
        });
    },
    methods: {
        isEditMode(question) {
            return question.isEditing;
        },
        isQuestionRequired(question = {}) {
            return Boolean(question.isRequired);
        },
        initSurveyTemplate(template, override) {
            if (this.survey.questions?.length && !override) {
                this.showOverrideWarning = template.title;
                return;
            } else {
                this.showOverrideWarning = false;
            }

            const relevantQuestions = this.questionBank
                .filter(q => template.questions?.includes(q._id))
                .map(skill => ({
                    skill,
                    isPaused: false,
                    isImpactDriver: false,
                    scaleType: ["structured", "structuredOnly"].includes(skill.answerType)
                        ? "nps"
                        : null,
                }));

            this.survey.questions = [
                // Remove old questions with the same skill id (reset) that were added through a template previously
                // and set everythign else to removed. This will allow old/running periods to continue being usable.
                ...this.survey.questions
                    .filter(question => !template.questions?.includes(question.skill?._id))
                    .map(question => ({ ...question, isRemoved: true })),
                ...relevantQuestions,
            ];
            // FIXME what if questions are empty?

            this.updateSurvey();
        },
        appendEmptyMultipleChoiceAnswers(question) {
            if (!question?.skill?.multipleChoiceOptions?.length) {
                // if we don't have option before(a new question)
                question.skill.multipleChoiceOptions = [
                    {
                        title: "",
                    },
                    {
                        title: "",
                    },
                    {
                        title: "",
                    },
                ];
                this.$forceUpdate();
            } else {
                // autoSave if user try to switch back to multiple chioce answer
                this.updateSurvey();
            }
        },
        addQuestionOrSection(type) {
            const newQuestion = {
                skill: {
                    _id: "",
                    name: "",
                    answerType: "unstructured",
                    type,
                    category: "null",
                    selected: true, // flag to signal whether question will be saved
                    multipleChoiceOptions: [], // for multiple choice questions
                    allowMultipleAnswers: false, // for multiple choice questions
                },
                isPaused: false,
                isPinned: false,
                scaleType: null,
                isEditing: true,
                unsaved: true, // flag to signal unsaved state if survey is not in draft mode
            };

            // add question

            this.survey.questions.push(newQuestion);
            this.$forceUpdate();

            setTimeout(() => {
                const customElements = this.$refs["custom"];
                if (customElements) {
                    customElements[customElements.length - 1].scrollIntoView(); // scroll the custom section into view
                }
            }, 200);
        },
        createCategory(categoryName, question) {
            this.$store
                .dispatch("surveys/surveys/createCategory", {
                    payload: {
                        name: categoryName,
                        surveyId: this.survey._id,
                        questionId: question._id,
                    },
                })
                .then(() => {
                    // UGLY FIXME
                    const editingIds = this.survey.questions
                        ?.filter(q => q.isEditing)
                        .map(q => q._id); // save our editing data

                    this.survey = this.getDetail(this.survey._id);
                    // reload detail because once the category is created, the question's category will be changed.

                    this.survey.questions?.forEach(function(part, index) {
                        this[index].isEditing = editingIds?.includes(part._id);
                    }, this.survey.questions); // restore editing data.

                    this.updateSurvey(); // TODO this should be done in BE.
                });
        },
        addQuestionFromLibrary(question) {
            const existedQuestion = this.survey.questions.find(q => q.skill?._id === question._id);
            if (existedQuestion) {
                if (existedQuestion.isRemoved) {
                    // question was added before but then removed. change it status only.
                    existedQuestion.isRemoved = false;
                } else {
                    return; // do nothing, question is already added.
                }
            } else {
                // add a new question object.
                this.survey.questions.push({
                    skill: question,
                    isPaused: false,
                    isImpactDriver: false,
                    scaleType: ["structured", "structuredOnly"].includes(question.answerType)
                        ? "nps"
                        : null,
                    isPinned: false,
                });
            }

            this.updateSurvey();
        },
        removeQuestion(question) {
            question.isRemoved = true;
            this.updateSurvey();
        },
        unpinQuestion(question) {
            question.isPinned = false;
            question.isPaused = false;
            this.updateSurvey();
        },
        pinQuestion(question) {
            question.isPaused = false;
            question.isPinned = true;
            this.updateSurvey();
        },
        unPauseQuestion(question) {
            question.isPaused = false;
            question.isPinned = false;
            this.updateSurvey();
        },
        pauseQuestion(question) {
            question.isPaused = true;
            question.isPinned = false;

            this.updateSurvey();
        },
        toggleImpactDriver(question) {
            question.isImpactDriver = !question.isImpactDriver;
            this.updateSurvey();
        },
        removeMultipleChoiceOption(question, index) {
            if (question.skill.multipleChoiceOptions.length <= 1) {
                return;
            }

            if (index < 0 || index >= question.skill.multipleChoiceOptions.length) {
                return;
            }

            Object.keys(question.skill.translations).forEach(locale => {
                if (question.skill.translations[locale].multipleChoiceOptions?.[index]) {
                    for (let i = index + 1; i < question.skill.multipleChoiceOptions.length; i++) {
                        if (question.skill.translations[locale].multipleChoiceOptions[i]) {
                            question.skill.translations[locale].multipleChoiceOptions[i - 1] =
                                question.skill.translations[locale].multipleChoiceOptions[i];
                        }
                    }

                    delete question.skill.translations[locale].multipleChoiceOptions[
                        question.skill.multipleChoiceOptions.length - 1
                    ];
                }
            });

            question.skill.multipleChoiceOptions.splice(index, 1);

            this.updateSurvey();
        },
        changeOrder(moved) {
            // swap them
            const questions = this.survey.questions;
            questions[moved.newIndex],
                (questions[moved.oldIndex] = questions[moved.oldIndex]),
                questions[moved.newIndex];
            this.updateSurvey();
        },

        onAnswerTypeChange(question, e) {
            const answerType = e.target.value;

            switch (answerType) {
                case "structured":
                case "structuredOnly":
                    question.scaleType = question.scaleType || "nps";
                    break;
                case "multipleChoice":
                    question.scaleType = null;
                    this.appendEmptyMultipleChoiceAnswers(question);
                    break;
                default:
                    question.scaleType = null;
            }

            this.updateSurvey();
        },

        debouncedUpdateSurvey: debounce(function() {
            this.updateSurvey();
        }, 1000),

        updateSurvey() {
            this.$emit("change", "questions", this.survey);
        },
        openPreview() {
            const lang = this.user.lang || "en";
            window.open(
                `/app/#/surveys/${this.$route.params.id}/answer/preview?lang=${lang}`,
                "_blank",
                "noopener noreferrer",
            );
        },
        scrollToBottom() {
            window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
        },
        onRequiresAnswerToggle(question) {
            if (!question) return;

            question.isRequired = !question.isRequired;
            this.updateSurvey();
        },

        openTranslationModal(skill, key) {
            this.key = key;
            this.currentSkill = skill;

            if (!this.currentSkill.translations) {
                this.currentSkill.translations = {};
            }

            this.translations = Object.keys(this.currentSkill.translations).reduce((acc, cur) => {
                acc[cur] = getVal(this.currentSkill.translations[cur], key);
                return acc;
            }, {});
            this.translations[this.locale] = getVal(this.currentSkill, key);

            if (!this.translations["en"]) {
                this.translations["en"] = "";
            }

            this.showTranslationModal = true;
        },

        updateSkillTranslation(skill, key) {
            if (!skill.translations) {
                skill.translations = {};
            }
            if (!skill.translations[this.locale]) {
                skill.translations[this.locale] = {};
            }

            if (key === "name") {
                skill.translations[this.locale].name = skill.name;
            } else if (key.startsWith("multipleChoiceOptions")) {
                if (!skill.translations[this.locale].multipleChoiceOptions) {
                    skill.translations[this.locale].multipleChoiceOptions = {};
                }
                if (!skill.translations[this.locale].multipleChoiceOptions[key.split(".")[1]]) {
                    skill.translations[this.locale].multipleChoiceOptions[key.split(".")[1]] = {};
                }

                skill.translations[this.locale].multipleChoiceOptions[key.split(".")[1]].title =
                    skill.multipleChoiceOptions[key.split(".")[1]].title;
            }
        },

        updateTranslations(translations) {
            // Add/Update current skill's translations
            Object.keys(translations).forEach(lang => {
                if (!this.currentSkill.translations[lang]) {
                    this.currentSkill.translations[lang] = {};
                }

                if (this.key === "name") {
                    this.currentSkill.translations[lang].name = translations[lang];
                } else if (this.key.startsWith("multipleChoiceOptions")) {
                    if (!this.currentSkill.translations[lang].multipleChoiceOptions) {
                        this.currentSkill.translations[lang].multipleChoiceOptions = {};
                    }
                    if (
                        !this.currentSkill.translations[lang].multipleChoiceOptions[
                            this.key.split(".")[1]
                        ]
                    ) {
                        this.currentSkill.translations[lang].multipleChoiceOptions[
                            this.key.split(".")[1]
                        ] = {};
                    }

                    this.currentSkill.translations[lang].multipleChoiceOptions[
                        this.key.split(".")[1]
                    ].title = translations[lang];
                }
            });

            if (this.key === "name") {
                this.currentSkill.name = translations[this.locale];
            } else if (this.key.startsWith("multipleChoiceOptions")) {
                this.currentSkill.multipleChoiceOptions[this.key.split(".")[1]].title =
                    translations[this.locale];
            }

            // Delete translations
            Object.keys(this.currentSkill.translations).forEach(lang => {
                if (!translations[lang]) {
                    if (this.key === "name") {
                        delete this.currentSkill.translations[lang].name;
                    } else if (this.key.startsWith("multipleChoiceOptions")) {
                        delete this.currentSkill.translations[lang].multipleChoiceOptions[
                            this.key.split(".")[1]
                        ];
                    }

                    if (!Object.keys(this.currentSkill.translations[lang]).length) {
                        delete this.currentSkill.translations[lang];
                    }
                }
            });

            this.showTranslationModal = false;

            this.updateSurvey();
        },
    },
};
</script>

<style scoped>
.input-group >>> .section {
    background-color: #fff;
}
.visibility-hidden {
    visibility: hidden;
}

.next-round {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-direction: row;
}

.next-round >>> .dropdown > button {
    padding: 0;
    margin-top: -3px;
}
.next-round >>> .dropdown > button:focus {
    background-color: inherit;
}
.next-round >>> .dropdown > button:hover {
    background-color: inherit;
}
.next-round >>> .dropdown > button > span {
    font-size: 16px !important;
}
</style>
